import { ref, inject, computed, type ComputedRef } from 'vue';
import EventBus from '@/components/mixins/event-bus-mixin';
import type { UseFileUploaderReturn } from './use-file-uploader';
import CONST from '@/globals/constants';
import type Utils from '@/globals/utils.js';

// jQuery is a global loaded via script tag; @types/jquery is not installed.
declare const $: any;

// ─── Composable ───────────────────────────────────────────────────────────────

/**
 * Isolates the jQuery overlay positioning logic for the file-upload dropzone.
 *
 * Owns `disableFileUploadListener` (formerly a `data()` flag) and subscribes
 * to the `SORTING_START` / `SORTING_END` EventBus events internally.
 *
 * Three overlay strategies mirror the three page types:
 * - **Schedule player selected** – positions dropzones over the schedule media
 *   list, splitting between the Regular and Smart-Adds zones.
 * - **Files / Alert / LED** – covers the media card grid area.
 * - **Project / Template / Audio** – covers the main page content area; only
 *   activates once the media-list modal is open.
 */
export function useDropzonePositioning (
  fileUploader: UseFileUploaderReturn,
  isMediaV2Enabled: ComputedRef<boolean>
) {
  /** When true the dropzone overlay is suppressed (e.g. during drag-sort). */
  const isUploadDisabled = ref(false);
  const $utils = inject('$utils') as typeof Utils;

  const scheduleActive = computed(() => fileUploader.usedForSchedulePagePlayerSelected.value);
  const filesActive = computed(() => fileUploader.usedForFilesPage.value ||
    fileUploader.usedForAlertPage.value ||
    fileUploader.usedForLEDPage.value ||
    fileUploader.usedForVideowallPage.value);
  const projectActive = computed(() => fileUploader.usedForProjectTemplatesPage.value ||
    fileUploader.usedForEditProjectTemplatePage.value ||
    fileUploader.usedForProjectAudioPage.value);

  // Listen to sort events from any component on the page.
  EventBus.$on('SORTING_START', () => { isUploadDisabled.value = true; });
  EventBus.$on('SORTING_END', () => { isUploadDisabled.value = false; });

  // ── Internal helper ─────────────────────────────────────────────────────────

  function containsFile (event: any): boolean {
    if (!event?.target) return false;

    const ignoredClasses = new Set([
      'el-tag',
      'el-tag-popId',
      'sortable-el',
      'el-tag-selected-schedule-tags'
    ]);
    const $target = $(event.target);
    for (const cls of ignoredClasses) {
      if ($target.hasClass(cls)) return false;
    }

    return (event as any).originalEvent?.dataTransfer?.types?.includes('Files') ?? false;
  }

  // ── Hide ────────────────────────────────────────────────────────────────────

  /**
   * Resets the file-uploader overlay to its hidden / inactive state.
   * Replaces `hideFileUploader()` on the component.
   */
  function hide (): void {
    const guideEl = $('#fileUploaderLayoutGuide');
    const uploaderEl = $('#fileUploader');

    uploaderEl.css({ zIndex: 1 });
    uploaderEl.removeClass('active');
    uploaderEl.css({ top: '0px', left: '0px', height: '0px', width: '0px' });
    uploaderEl.addClass('');
    guideEl.find('.file-dropzone').each((_i: any, el: any) => {
      $(el).css({ height: '0px', width: '0px' });
    });
    guideEl.removeClass('active').removeAttr('style');
    uploaderEl.removeClass('active').addClass('not-active').removeAttr('style');

    // Debug mode: add a visible outline for development inspection.
    if (localStorage.getItem('aryaFileUploadsDebug') &&
        JSON.parse(localStorage.getItem('aryaFileUploadsDebug')!)) {
      uploaderEl.addClass('debug');
    }
  }

  // ── Per-page position adjustors ─────────────────────────────────────────────

  function adjustSchedulePagePosition (isActive: boolean): void {
    if (!isActive) { hide(); return; }

    const guideEl = $('#fileUploaderLayoutGuide');
    const uploaderEl = $('#fileUploader');
    const isForModal = uploaderEl.hasClass('set-dropzone-in-media-list-modal');
    const modalEl = isForModal ? $('#mediaList') : $({});

    const targetPlayerId = $('#scheduleItemFullDetails')[0]
      .className.toString().match(/(\bplayerId-\S+\b)/ig)![0].substring(9);
    const mediaContentEl = $('#scheduleItemFullDetails').find('.media-content');
    const headerMarginBottom = $utils.is1280View() ? 32 : 53;
    const guideTop = mediaContentEl.offset()!.top - headerMarginBottom;

    guideEl.addClass('active').css({
      top: `${guideTop}px`,
      left: '0px',
      height: `${mediaContentEl.height()! + headerMarginBottom + 8}px`,
      width: `${mediaContentEl.offset()!.left + mediaContentEl.width()!}px`
    });

    if (isForModal) {
      const optionsEl = modalEl.find('.options').length ? modalEl.find('.options') : null;
      const offset = 20;
      guideEl.css({
        top: `${optionsEl ? (modalEl.offset()!.top + optionsEl.outerHeight()!) : modalEl.offset()!.top}px`,
        left: `${modalEl.offset()!.left + offset}px`,
        height: `${optionsEl ? (modalEl.height()! - optionsEl.outerHeight()!) : modalEl.height()!}px`,
        width: `${modalEl.width()! - offset * 2}px`
      });
    }

    uploaderEl.addClass('active').removeClass('not-active').css({
      top: `${guideEl.offset()!.top}px`,
      left: `${guideEl.offset()!.left}px`,
      height: `${guideEl.height()!}px`,
      width: `${guideEl.width()!}px`,
      zIndex: 999999
    });

    const smartAddsEl: any = $('#smartAddsScheduleSortGroupWrapper').length
      ? $('#smartAddsScheduleSortGroupWrapper')
      : false;
    const regularEl: any = $('#regularScheduleSortGroupWrapper').length
      ? $('#regularScheduleSortGroupWrapper')
      : false;
    const smartAddsAbove = smartAddsEl && (smartAddsEl.offset()!.top < guideEl.offset()!.top);
    const smartAddsHidden = !smartAddsEl || !smartAddsEl.length ||
      (smartAddsEl.length && ((smartAddsEl.offset()!.top + smartAddsEl.height()!) < guideEl.offset()!.top));

    if (regularEl) {
      let regularStyle: Record<string, unknown> = {
        top: `${Math.max(0, guideEl.offset()!.top - regularEl.offset()!.top)}px`,
        height: `${guideEl.height()!}px`,
        width: `${guideEl.width()!}px`,
        zIndex: 99999
      };

      if (smartAddsEl) {
        regularStyle = {
          top: `${Math.max(0, headerMarginBottom + smartAddsEl.height()!)}px`,
          height: `${guideEl.height()! - Math.max(0, headerMarginBottom + smartAddsEl.height()!)}px`,
          zIndex: 99999
        };
      }
      if (smartAddsAbove) {
        const h = (smartAddsEl as any).offset()!.top + (smartAddsEl as any).height()! - guideEl.offset()!.top;
        regularStyle = { top: `${h}px`, height: `${guideEl.height()! - h}px`, zIndex: 99999 };
      }
      if (smartAddsHidden) {
        regularStyle = { top: '0px', height: '100%' };
      }
      if (isForModal) {
        regularStyle = { top: '0px', left: '0px', height: '100%', width: '100%', zIndex: 99999 };
        if (regularEl.hasClass('disabled-uploader')) {
          regularStyle = { top: '0px', left: '0px', height: '0px', width: '0px', zIndex: 0 };
        }
      }

      guideEl.find('#regularScheduleFileDropzone').css(regularStyle as any);
      $('#fileUploaderRegularMedia').css(regularStyle as any)
        .removeClass((_: any, cls: any) => cls.split(/\s+/).filter((c: any) => c.startsWith('target-playerId')).join(' '))
        .addClass(`target-playerId-${targetPlayerId}`);
    }

    if (smartAddsEl) {
      let smartStyle: Record<string, unknown> = {
        top: `${Math.max(0, guideTop - (smartAddsEl as any).offset()!.top)}px`,
        height: `${(smartAddsEl as any).height()! + headerMarginBottom}px`,
        width: `${guideEl.width()!}px`,
        zIndex: 99999,
        border: 'none'
      };
      if (smartAddsAbove) {
        smartStyle = {
          top: '0px',
          height: `${(smartAddsEl as any).offset()!.top + (smartAddsEl as any).height()! - guideEl.offset()!.top}px`,
          width: `${guideEl.width()!}px`,
          zIndex: 99999,
          border: 'none'
        };
      }
      if (smartAddsHidden) {
        smartStyle = { top: '0px', left: '0px', height: '0px', width: '0px', zIndex: 0 };
      }
      if (isForModal) {
        smartStyle = { top: '0px', height: '100%', width: '100%', zIndex: 99999 };
        if ((smartAddsEl as any).hasClass('disabled-uploader')) {
          smartStyle = { top: '0px', height: '0px', width: '0px', zIndex: 0 };
        }
      }

      guideEl.find('#smartAddsScheduleFileDropzone').css(smartStyle as any);
      $('#fileUploaderSmartAdds').css(smartStyle as any)
        .removeClass((_: any, cls: any) => cls.split(/\s+/).filter((c: any) => c.startsWith('target-playerId')).join(' '))
        .addClass(`target-PlayerId-${targetPlayerId}`);
    } else {
      $('#fileUploaderSmartAdds').css({ width: '0px', height: '0px', zIndex: 900 });
    }
  }

  function adjustFilesPagePosition (isActive: boolean): void {
    if (!isActive) { hide(); return; }

    const guideEl = $('#fileUploaderLayoutGuide');
    const uploaderEl = $('#fileUploader');
    let containerEl = $('#media-page');
    if (
      !isMediaV2Enabled.value &&
      fileUploader.usedForLEDPage.value &&
      fileUploader.currentRouteName.value === CONST.ROUTE_NAME_LED
    ) {
      // TODO(media-v2-cleanup): Remove this legacy LED page
      containerEl = $('#ledConfiguration');
    }

    const applyPosition = (style: Record<string, unknown>) => {
      guideEl.addClass('active').css(style);
      uploaderEl.addClass('active').removeClass('not-active').css({ ...style, zIndex: 999999 });
    };

    if (fileUploader.isMediaPage.value && isMediaV2Enabled.value) {
      const listEl = containerEl.find('#media-list-container');
      const filterEl = containerEl.find('#media-list-filter-container');
      applyPosition({
        top: `${filterEl.offset()!.top + filterEl.innerHeight()!}px`,
        left: `${listEl.offset()!.left}px`,
        height: `${$(window).height()! - (filterEl.offset()!.top + filterEl.innerHeight()!)}px`,
        width: `${listEl.width()!}px`
      });
    } else {
      const mainEl = containerEl.find('.cpl-main');
      const optionsEl = mainEl.find('.options');
      applyPosition({
        top: `${optionsEl.offset()!.top + optionsEl.innerHeight()!}px`,
        left: `${mainEl.offset()!.left}px`,
        height: `${$(window).height()! - (optionsEl.offset()!.top + optionsEl.innerHeight()!)}px`,
        width: `${mainEl.width()! + containerEl.find('.cpl-aside').width()!}px`
      });
    }
  }

  function adjustProjectPagePosition (isActive: boolean): void {
    if (!isActive) { hide(); return; }

    // Only activatable once the media-list modal is shown.
    if (
      (fileUploader.usedForProjectTemplatesPage.value || fileUploader.usedForEditProjectTemplatePage.value) &&
      !fileUploader.config.setDropzoneInMediaListModal
    ) return;

    const guideEl = $('#fileUploaderLayoutGuide');
    const uploaderEl = $('#fileUploader');
    const isForModal = uploaderEl.hasClass('set-dropzone-in-media-list-modal');
    const mainEl = $('.page.white').find('.cpl-main');
    const optionsEl = mainEl.find('.options');

    guideEl.addClass('active').css({
      top: `${optionsEl.offset()!.top + optionsEl.innerHeight()!}px`,
      left: `${mainEl.offset()!.left}px`,
      height: `${$(window).height()! - (optionsEl.offset()!.top + optionsEl.innerHeight()!)}px`,
      width: `${mainEl.width()! + $('.page.white').find('.cpl-aside').width()!}px`
    });

    if (isForModal) {
      const modalEl = $('#mediaList');
      const modalOptions = modalEl.find('.options').length ? modalEl.find('.options') : null;
      const offset = 20;
      guideEl.css({
        top: `${modalOptions ? (modalEl.offset()!.top + modalOptions.outerHeight()!) : modalEl.offset()!.top}px`,
        left: `${modalEl.offset()!.left + offset}px`,
        height: `${modalOptions ? (modalEl.height()! - modalOptions.outerHeight()!) : modalEl.height()!}px`,
        width: `${modalEl.width()! - offset * 2}px`
      });
    }

    uploaderEl.addClass('active').removeClass('not-active').css({
      top: `${guideEl.offset()!.top}px`,
      left: `${guideEl.offset()!.left}px`,
      height: `${guideEl.height()!}px`,
      width: `${guideEl.width()!}px`,
      zIndex: 999999
    });
  }

  // ── Public API ───────────────────────────────────────────────────────────────

  /**
   * Main entry point. Replaces `handleUploadDropzonePosition(params)` on the
   * component.
   *
   * - `'watchDragFiles'` – attaches drag-start/-enter/-leave/-end listeners.
   * - `'inActive'` – immediately hides the overlay.
   * - `null` / anything else – no-op (use `hide()` directly when needed).
   */
  function adjust (action: 'watchDragFiles' | 'inActive' | null): void {
    const adjustActive = (on: boolean) => {
      if (scheduleActive.value) adjustSchedulePagePosition(on);
      else if (filesActive.value) adjustFilesPagePosition(on);
      else if (projectActive.value) adjustProjectPagePosition(on);
    };

    if (action === 'watchDragFiles') {
      $(document)
        .on('dragstart dragenter', (event: any) => {
          adjustActive(!isUploadDisabled.value && containsFile(event));
        })
        .on('dragleave dragend', (event: any) => {
          if (event.clientX < 1 && event.clientY < 1) adjustActive(false);
        });
    }

    if (action === 'inActive' || isUploadDisabled.value) {
      hide();
    }
  }

  return {
    /** Suppresses the overlay during drag-sort operations. */
    isUploadDisabled,
    adjust,
    hide
  };
}
